import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordValidator from 'password-validator';
import axios from 'axios';
import { debounce } from 'lodash';
import { validate as isValidEmail } from 'email-validator';
import { Pathname } from 'Routes';
import { dataFetcher, endpoints, baseUrl } from 'Api';
import { AuthContext, AppContext } from 'Context';
import { Button, Snackbar } from 'Components';
import RotatingLoader from 'Components/RotatingLoader';
import { Input } from '../components/input-field';
import { AuthPage } from '../components/page';
import authStyles from '../auth.module.css';
import styles from './index.module.css';

const passwordSchema = new PasswordValidator()
  .is()
  .min(12)
  .is()
  .max(30)
  .has()
  .symbols(1)
  .has()
  .digits(1)
  .has()
  .uppercase(1)
  .lowercase(1);
const defaultSnackbar = {
  isVisible: false,
  type: undefined,
  message: undefined,
};

const isMobileDevice = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());
  const isSmallScreen = window.innerWidth <= 767;
  return isMobileUA || isSmallScreen;
};

export const SetPassword = () => {
  const navigate = useNavigate();
  const confirmPasswordRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { setToken, forceAutoSignin } = useContext(AuthContext);
  const { setAppSnackbar } = useContext(AppContext);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [temporaryToken, setTemporaryToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [noPassword, setNoPassword] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Gift receive flow state
  const isGiftFlow = searchParams.get('gift') === 'true';
  const giftFromName = searchParams.get('fromName') || 'Someone special';
  const [userEmail, setUserEmail] = useState('');
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [showGiftPopup, setShowGiftPopup] = useState(isGiftFlow);
  const [showCelebration, setShowCelebration] = useState(isGiftFlow);
  const [celebrationFading, setCelebrationFading] = useState(false);
  const [giftContentStep, setGiftContentStep] = useState('celebration');
  const [confirmEmailInput, setConfirmEmailInput] = useState('');
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);
  const [showOpenAppPopup, setShowOpenAppPopup] = useState(false);
  const [showProjectorVideo, setShowProjectorVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const projectorVideoUrl = 'https://projector-optimized.s3.us-west-2.amazonaws.com/production/background-videos/Web_lq.mp4';
  const emailConfirmed = confirmEmailInput && userEmail && confirmEmailInput.trim().toLowerCase() === userEmail.trim().toLowerCase();

  // Debounced email match check — fires after user stops typing
  const debouncedEmailMatch = useCallback(
    debounce((typed) => {
      if (!typed) {
        setEmailVerifying(false);
        setEmailMismatch(false);
        return;
      }
      const matches = userEmail && typed.trim().toLowerCase() === userEmail.trim().toLowerCase();
      setEmailMismatch(!matches);
      setEmailVerifying(false);
    }, 800),
    [userEmail]
  );

  // Debounced email check - calls signinEmail API to verify if email is already used
  const debouncedEmailCheck = useCallback(
    debounce((emailToCheck) => {
      if (!isValidEmail(emailToCheck)) {
        setCheckingEmail(false);
        setEmailExists(null);
        return;
      }
      setCheckingEmail(true);
      dataFetcher(endpoints.signinEmail, { email: emailToCheck })
        .then(({ success }) => {
          setEmailExists(success);
          setCheckingEmail(false);
        })
        .catch(() => {
          setEmailExists(null);
          setCheckingEmail(false);
        });
    }, 750),
    []
  );

  // Reset zoom and scroll before paint so gift animation displays correctly
  useLayoutEffect(() => {
    if (!isGiftFlow) return;
    window.scrollTo(0, 0);
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const original = viewportMeta?.getAttribute('content');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no');
    }
    return () => {
      if (viewportMeta && original) {
        viewportMeta.setAttribute('content', original);
      }
    };
  }, [isGiftFlow]);

  // Lock body scroll when gift popup is open so background doesn't scroll
  useEffect(() => {
    if (showGiftPopup) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showGiftPopup]);

  // Auto-hide celebration particles — fade out then show projector video
  useEffect(() => {
    if (!showCelebration) return;

    const fadeTimer = setTimeout(() => {
      setCelebrationFading(true);
    }, 7000);

    const removeTimer = setTimeout(() => {
      setShowCelebration(false);
      setCelebrationFading(false);
      setShowProjectorVideo(true);
    }, 8000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [showCelebration]);

  // Show "Open in App" popup on mobile immediately when page loads
  // TODO: Will be used in future
  // useEffect(() => {
  //   if (isGiftFlow && isMobileDevice()) {
  //     setShowOpenAppPopup(true);
  //   }
  // }, [isGiftFlow]);

  // Extract token from URL and verify with API
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setTemporaryToken(token);
      // console.log('Token extracted from URL:', token);

      // Call signinEmail API to check if user needs to set password
      // Using direct axios call to avoid interceptor overwriting the token
      setIsLoading(true);
      axios
        .post(`${baseUrl}${endpoints.signinEmail}`, { token })
        .then(({ data }) => {
          // console.log('========== signinEmail API Response ==========');
          // console.log('Full Response:', data);
          // console.log('Success:', data.success);
          // console.log('No Password:', data.no_password);
          // console.log('Message:', data.message);
          // console.log('==============================================');

          if (data.success && data.no_password !== undefined) {
            setNoPassword(data.no_password);
          }
          // Fetch user's current email from profile
          if (isGiftFlow) {
            axios
              .post(`${baseUrl}${endpoints.getMyProfile}`, { token })
              .then(({ data: profileData }) => {
                const email = profileData?.data?.email || '';
                setUserEmail(email);
                setNewEmail(email);
              })
              .catch(() => {});
          }
          setIsLoading(false);
        })
        .catch((error) => {
          // console.error('========== signinEmail API Error ==========');
          // console.error('Error:', error);
          // console.error('Error Response:', error.response?.data);
          // console.error('===========================================');
          setIsLoading(false);
          setSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Invalid or expired token',
          });
        });
    } else {
      // console.log('No token found in URL');
      setIsLoading(false);
    }
  }, [searchParams]);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const isPasswordValid = passwordSchema.validate(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const passwordHealth = (
    password
      ? password.length < 12 || !hasUpperCase
        ? 'weak'
        : passwordSchema.validate(password)
          ? password.length >= 12
            ? 'strong'
            : 'medium'
          : 'weak'
      : ''
  );

  const handleNext = async (e) => {
    e.preventDefault();

    if (!password) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Please enter a password',
      });
      return;
    }

    if (!isPasswordValid) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Password does not meet requirements',
      });
      return;
    }

    if (password.length > 30) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Password cannot exceed 30 characters',
      });
      return;
    }

    if (!passwordsMatch) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Passwords do not match',
      });
      return;
    }

    // Call setPassword API
    // Using direct axios call to avoid interceptor overwriting the token
    setSubmitLoading(true);
    try {
      const payload = {
        token: temporaryToken,
        password,
      };

      // console.log('========== Set Password API Request ==========');
      // console.log('Payload:', payload);
      // console.log('==============================================');

      const { data } = await axios.post(`${baseUrl}${endpoints.setPassword}`, payload);

      // console.log('========== Set Password API Response ==========');
      // console.log('Full Response:', data);
      // console.log('Success:', data.success);
      // console.log('Token:', data.data?.token);
      // console.log('===============================================');

      if (data.success && data.data?.token) {
        // Store token in localStorage
        setToken(data.data.token);

        // Show success message (matching login/signup flow)
        setAppSnackbar({
          message: 'Password set successfully',
          isVisible: true,
        });

        // Check if new user from API response (matching social login logic)
        const isNewUser = data.new_user || data.data?.is_new_user;

        // Wait a moment, then trigger auto sign-in and navigate
        setTimeout(() => {
          forceAutoSignin();

          // Navigate to avatar for new users (matching social login flow)
          // Navigate to access for existing users
          if (isNewUser) {
            navigate(`${Pathname.authentication.avatar}${isGiftFlow ? '?gift=true' : ''}`, { state: { fromSetPassword: true, isGiftFlow } });
          } else {
            // Force page reload to ensure proper authentication state
            window.location.href = '/access';
          }
        }, 500);
      } else {
        setSnackbar({
          isVisible: true,
          type: 'error',
          message: data.message || 'Failed to set password',
        });
        setSubmitLoading(false);
      }
    } catch (error) {
      // console.error('========== Set Password API Error ==========');
      // console.error('Error:', error);
      // console.error('Error Response:', error.response?.data);
      // console.error('============================================');

      setSnackbar({
        isVisible: true,
        type: 'error',
        message: error.response?.data?.message || (typeof error.message === 'string' ? error.message : 'An error occurred while setting password'),
      });
      setSubmitLoading(false);
    }
  };

  const handlePasswordInput = (event) => {
    const inputPassword = event.target.value;
    if (inputPassword.length > 30) {
      setPassword(inputPassword.slice(0, 30));
    } else {
      setPassword(inputPassword);
    }
  };

  // Transition gift popup content from celebration → password form
  const handleDismissGiftPopup = () => {
    setShowCelebration(false);
    setCelebrationFading(false);
    setGiftContentStep('password');
    setTimeout(() => {
      window.scrollTo(0, 0);
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no');
      }
    }, 100);
  };

  // Dismiss projector video and continue to email confirmation
  const handleDismissProjectorVideo = () => {
    setShowProjectorVideo(false);
  };

  // Open app via custom URL scheme; fall back to App Store / Play Store if not installed
  const handleOpenApp = () => {
    const params = new URLSearchParams(window.location.search);
    const appUrl = `projector://setpassword?${params.toString()}`;

    const ua = (navigator.userAgent || '').toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const storeUrl = isIOS
      ? 'https://apps.apple.com/in/app/projector-app/id1585850104'
      : 'https://play.google.com/store/apps/details?id=com.projectorllc.projectorapp';

    // Use visibilitychange to detect if app opened (page goes to background).
    // The old Date.now() timing hack is unreliable on iOS Safari — the setTimeout
    // fires before the app finishes launching and redirects to the store.
    let appOpened = false;
    const onVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    window.location.href = appUrl;

    // If page is still visible after 3s, app is not installed → go to store
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (!appOpened) {
        window.location.href = storeUrl;
      }
    }, 3000);
  };

  // Dismiss "Open in App" popup and continue in browser
  const handleContinueInBrowser = () => {
    setShowOpenAppPopup(false);
  };

  // Auto-advance to password step when email is confirmed
  useEffect(() => {
    if (emailConfirmed && giftContentStep === 'celebration' && !emailVerifying) {
      const timer = setTimeout(() => {
        handleDismissGiftPopup();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [emailConfirmed, giftContentStep, emailVerifying]);

  // Confirm email → call API if email changed, then proceed to set password
  const [changingEmail, setChangingEmail] = useState(false);
  const handleEmailConfirm = async () => {
    const emailChanged = isEditingEmail && newEmail !== userEmail && isValidEmail(newEmail);
    if (emailChanged) {
      setChangingEmail(true);
      try {
        await axios.post(`${baseUrl}${endpoints.changeEmailBeforPasswordSet}`, {
          token: temporaryToken,
          email: newEmail,
        });
        setChangingEmail(false);
        setShowEmailConfirm(false);
      } catch (error) {
        setChangingEmail(false);
        setSnackbar({
          isVisible: true,
          type: 'error',
          message: error.response?.data?.message || 'Failed to update email',
        });
      }
    } else {
      setShowEmailConfirm(false);
    }
  };

  // Render email confirmation screen (before celebration)
  const renderEmailConfirmScreen = () => {
    if (!isGiftFlow) return null;

    return (
      <div className={`${styles.emailConfirmOverlay} ${showEmailConfirm ? styles.emailConfirmOverlayVisible : ''}`}>
        {/* Ambient background glow orbs */}
        <div className={`${styles.emailConfirmGlow} ${styles.emailConfirmGlow1}`} />
        <div className={`${styles.emailConfirmGlow} ${styles.emailConfirmGlow2}`} />

        <div className={styles.emailConfirmCard}>
          {/* Animated top accent line */}
          <div className={styles.emailConfirmAccent} />

          <div className={styles.emailConfirmContent}>
            {/* Sender badge */}
            {/* <div className={styles.emailConfirmFromBadge}>
              <span className={styles.emailConfirmFromLabel}>From</span>
              <span className={styles.emailConfirmFromName}>{giftFromName}</span>
            </div> */}

            <h1 className={styles.emailConfirmTitle}>
              {`${giftFromName} Has Gifted You Projector Memories!`}
            </h1>
            <p className={styles.emailConfirmDescription}>
              {`${giftFromName} has sent Projector Memories to ${userEmail || 'your email'}. Would you like to continue with this email or use a different one?`}
            </p>

            {/* Email display or edit */}
            <div className={styles.emailConfirmInputWrap}>
              <label className={styles.emailConfirmLabel}>Deliver to</label>
              {isEditingEmail ? (
                <div className={styles.emailConfirmInputRow}>
                  <input
                    type="email"
                    className={styles.emailConfirmInput}
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setEmailExists(null);
                      if (isValidEmail(e.target.value)) {
                        setCheckingEmail(true);
                        debouncedEmailCheck(e.target.value);
                      } else {
                        setCheckingEmail(false);
                      }
                    }}
                    placeholder="Enter your email address"
                    autoFocus
                  />
                  {checkingEmail && (
                    <div className={styles.emailSpinner}>
                      <div className={styles.emailSpinnerRing} />
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.emailConfirmEmailDisplay}>
                  {userEmail || 'your current email'}
                </div>
              )}
              {isEditingEmail && emailExists === true && (
                <p className={styles.emailExistsMsg}>An account with this email already exists. Please use a different email.</p>
              )}
              <button
                type="button"
                className={styles.emailConfirmChangeLink}
                onClick={() => {
                  setIsEditingEmail(!isEditingEmail);
                  if (isEditingEmail) {
                    setNewEmail(userEmail);
                    setEmailExists(null);
                    setCheckingEmail(false);
                  }
                }}
              >
                {isEditingEmail ? 'Cancel' : 'Use a different email'}
              </button>
            </div>

            <button
              type="button"
              className={styles.emailConfirmButton}
              onClick={handleEmailConfirm}
              disabled={changingEmail || (isEditingEmail && newEmail !== userEmail && (checkingEmail || emailExists === true || !isValidEmail(newEmail)))}
            >
              {changingEmail ? 'Updating...' : checkingEmail ? 'Checking...' : isEditingEmail && newEmail !== userEmail ? 'Confirm & Continue' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render gift celebration overlay + sliding popup
  const renderGiftCelebration = () => {
    if (!isGiftFlow) return null;

    return (
      <>
        {/* Full-page loader for gift flow password submit - must be outside giftPopup
            because giftPopup's CSS transform creates a new containing block that
            prevents position:fixed from covering the full viewport */}
        {giftContentStep === 'password' && submitLoading && <RotatingLoader dark />}

        {/* Celebration confetti & fireworks */}
        {showCelebration && (
          <div className={`${styles.celebrationCanvas} ${celebrationFading ? styles.celebrationCanvasFading : ''}`}>
            {/* Confetti pieces — reduced on mobile for performance */}
            {Array.from({ length: window.innerWidth <= 767 ? 60 : 300 }, (_, i) => {
              const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6b9d', '#c9b1ff'];
              const fallClasses = [
                '', styles.confettiFall2, styles.confettiFall3,
                styles.confettiFall4, styles.confettiFall5, styles.confettiFall6,
              ];
              const isRound = i % 3 !== 0;
              const w = isRound ? 8 + (i % 5) * 2 : 6 + (i % 4) * 2;
              const h = isRound ? w : 8 + (i % 5) * 2;
              return (
                <div
                  key={`confetti-${i}`}
                  className={`${styles.confettiPiece} ${fallClasses[i % 6]}`}
                  style={{
                    '--left': `${((i * 7.3) % 100).toFixed(1)}%`,
                    '--color': colors[i % colors.length],
                    '--duration': `${(2.5 + ((i * 3) % 11) / 10).toFixed(1)}s`,
                    '--delay': `${(((i * 13) % 100) / 100).toFixed(2)}s`,
                    '--w': `${w}px`,
                    '--h': `${h}px`,
                    '--radius': isRound ? '50%' : '2px',
                  }}
                />
              );
            })}

            {/* Firework burst group 1 - left */}
            <div className={`${styles.fireworkGroup1}`} style={{ position: 'absolute' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={`burst1-${i}`} className={styles.fireworkBurst} />
              ))}
            </div>

            {/* Firework burst group 2 - right */}
            <div className={`${styles.fireworkGroup2}`} style={{ position: 'absolute' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={`burst2-${i}`} className={styles.fireworkBurst} />
              ))}
            </div>

            {/* Firework burst group 3 - center */}
            <div className={`${styles.fireworkGroup3}`} style={{ position: 'absolute' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={`burst3-${i}`} className={styles.fireworkBurst} />
              ))}
            </div>

            {/* Sparkle dots */}
            {Array.from({ length: 8 }, (_, i) => (
              <div key={`sparkle-${i}`} className={styles.sparkle} />
            ))}

            {/* Firework rockets + bursts from bottom — fewer on mobile */}
            {(window.innerWidth <= 767 ? [
              { left: '15%', rx: 30, ry: 72, delay: 3.6 },
              { left: '40%', rx: 8, ry: 75, delay: 3.7 },
              { left: '60%', rx: -8, ry: 75, delay: 3.55 },
              { left: '85%', rx: -30, ry: 72, delay: 3.4 },
            ] : [
              { left: '5%', rx: 40, ry: 65, delay: 3.4 },
              { left: '15%', rx: 30, ry: 72, delay: 3.6 },
              { left: '25%', rx: 18, ry: 68, delay: 3.5 },
              { left: '35%', rx: 8, ry: 75, delay: 3.7 },
              { left: '45%', rx: 3, ry: 70, delay: 3.45 },
              { left: '55%', rx: -3, ry: 70, delay: 3.55 },
              { left: '65%', rx: -8, ry: 75, delay: 3.65 },
              { left: '75%', rx: -18, ry: 68, delay: 3.5 },
              { left: '85%', rx: -30, ry: 72, delay: 3.4 },
              { left: '95%', rx: -40, ry: 65, delay: 3.6 },
            ]).map((r, idx) => (
              <React.Fragment key={`fw-${idx}`}>
                <div
                  className={styles.rocketWrap}
                  style={{
                    left: r.left,
                    '--rx': `${r.rx}px`,
                    '--ry': `-${r.ry}vh`,
                    '--rdelay': `${r.delay}s`,
                  }}
                >
                  <div className={styles.rocketTrail} />
                </div>
                <div
                  className={styles.burstWrap}
                  style={{
                    left: r.left,
                    bottom: `${r.ry}vh`,
                    '--bx': `${r.rx}px`,
                    '--bdelay': `${r.delay + 1.3}s`,
                  }}
                >
                  {Array.from({ length: window.innerWidth <= 767 ? 6 : 12 }, (_, i) => (
                    <div key={i} className={styles.burstSpark} />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Dark overlay */}
        {showGiftPopup && (
          <div
            className={`${styles.giftOverlay} ${styles.giftOverlayVisible}`}
          />
        )}

        {/* Gift popup - celebration slides up, password fields appear below */}
        {showGiftPopup && (
        <div className={`${styles.giftPopup} ${styles.giftPopupVisible}`}>
          {/* Open in App popup (mobile only, after celebration fades) */}
          {showOpenAppPopup && (
            <div className={styles.openAppOverlay}>
              <div className={styles.openAppCard}>
                <div className={styles.openAppEmoji}>🎁</div>
                <h2 className={styles.openAppTitle}>Open in Projector App</h2>
                <p className={styles.openAppDescription}>
                  For the best experience, open this gift in the Projector app
                </p>
                <button
                  type="button"
                  className={styles.openAppButton}
                  onClick={handleOpenApp}
                >
                  Open App
                </button>
                <button
                  type="button"
                  className={styles.openAppDismiss}
                  onClick={handleContinueInBrowser}
                >
                  Continue in Browser
                </button>
              </div>
            </div>
          )}
          <div className={`${styles.giftPopupContent} ${giftContentStep === 'password' ? styles.giftContentCompact : ''}`}>
            {/* Celebration header - always visible, slides up in password step */}
            <div className={styles.giftIconWrapper}>
              {Array.from({ length: 10 }, (_, i) => (
                <div key={`cracker-${i}`} className={styles.crackerParticle} />
              ))}
              <div className={styles.boxGlow} />
              <div className={styles.giftIcon}>🎁</div>
            </div>
            <h1 className={styles.giftTitle}>You Have Received Projector Memories!</h1>
            <p className={styles.giftSubtitle}>{`${giftFromName} has sent you a gift!`}</p>
            <p className={styles.giftDescription}>
              Never miss a memory
            </p>

            {/* Email section + button - only visible during celebration step */}
            {giftContentStep === 'celebration' && (
              <>
                <div className={styles.giftEmailWrap}>
                  <label className={styles.giftEmailLabel}>Confirm your email to continue</label>
                  <div className={styles.giftEmailInputRow}>
                    <input
                      type="email"
                      className={styles.giftEmailInput}
                      value={confirmEmailInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        setConfirmEmailInput(val);
                        setEmailMismatch(false);
                        if (val) {
                          setEmailVerifying(true);
                          debouncedEmailMatch(val);
                        } else {
                          setEmailVerifying(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && emailConfirmed) {
                          e.preventDefault();
                          handleDismissGiftPopup();
                        }
                      }}
                      placeholder="Enter your email address"
                    />
                    {emailVerifying && (
                      <span className={styles.giftEmailSpinner}>
                        <span className={styles.giftEmailSpinnerRing} />
                      </span>
                    )}
                    {!emailVerifying && emailConfirmed && (
                      <span className={styles.giftEmailTick}>&#10003;</span>
                    )}
                  </div>
                  {emailMismatch && !emailVerifying && (
                    <p className={styles.giftEmailError}>Email does not match. Please try again.</p>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.giftContinueButton}
                  onClick={handleDismissGiftPopup}
                  disabled={!emailConfirmed}
                >
                  Let&apos;s Get Started
                </button>
              </>
            )}

            {/* Password fields - appear below celebration content */}
            {giftContentStep === 'password' && (
              <div className={`${styles.giftPasswordFields} ${styles.giftContentFadeIn}`}>
                {noPassword === false ? (
                  /* Password already set - show message and login button inside fireworks popup */
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <h2
                      style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        marginBottom: '8px',
                        color: '#ffffff',
                      }}
                    >
                      Password Already Set
                    </h2>
                    <p
                      style={{
                        fontSize: '14px',
                        color: '#b0b0b0',
                        lineHeight: '1.4',
                        margin: '0 0 24px 0',
                      }}
                    >
                      You already have a password. Please login with your existing credentials.
                    </p>
                    <Button
                      blockText
                      isBlue
                      isFullWidth
                      isLarge
                      onClick={() => navigate('/newSign-in')}
                    >
                      Go to Login
                    </Button>
                  </div>
                ) : (
                  /* Password not set - show password form */
                  <>
                    <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} />
                    <form onSubmit={handleNext}>
                      <div className={styles.formField} style={{ marginBottom: '16px' }}>
                        <Input
                          label="Create Password"
                          type="password"
                          automaticFocus
                          value={password}
                          onInput={handlePasswordInput}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              confirmPasswordRef.current?.focus();
                            }
                          }}
                          onPaste={(e) => {
                            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                            if (pastedText.length > 30) {
                              e.preventDefault();
                            }
                            const totalLength = e.target.value.length + pastedText.length;
                            if (totalLength > 30) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const inputPassword = e.target.value;
                            if (inputPassword.length > 30) {
                              const trimmedText = inputPassword.slice(0, 30);
                              e.target.value = trimmedText;
                              setPassword(trimmedText);
                            }
                          }}
                          placeholder="Enter password"
                          minLength={12}
                          maxLength={30}
                          required
                        />
                      </div>

                      <div className={styles.formField} style={{ marginBottom: '16px' }}>
                        <Input
                          label="Confirm Password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm password"
                          required
                          inputRef={confirmPasswordRef}
                          helperText={
                            confirmPassword
                            && !passwordsMatch && (
                              <span className={authStyles.errorText}>
                                Passwords do not match
                              </span>
                            )
                          }
                        />
                      </div>

                      <div
                        className={styles.formField}
                        style={{
                          marginBottom: '16px',
                          padding: '12px',
                          backgroundColor: 'rgba(0, 117, 255, 0.1)',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#b0b0b0',
                        }}
                      >
                        <p
                          style={{
                            margin: '0 0 6px 0',
                            fontWeight: '600',
                            color: '#ffffff',
                            fontSize: '13px',
                            textAlign: 'left',
                          }}
                        >
                          Password Requirements:
                        </p>
                        <ul
                          style={{
                            margin: '0',
                            paddingLeft: '18px',
                            lineHeight: '1.6',
                            textAlign: 'left',
                          }}
                        >
                          <li style={{ color: password && password.length >= 12 && password.length <= 30 ? '#23e223' : '#b0b0b0' }}>12 - 30 Characters</li>
                          <li style={{ color: password && hasUpperCase ? '#23e223' : '#b0b0b0' }}>Minimum of one uppercase</li>
                          <li style={{ color: password && password.match(/[a-z]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one lowercase</li>
                          <li style={{ color: password && password.match(/[0-9]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one number</li>
                          <li style={{ color: password && password.match(/[!@#$%^&*()_+|~=`{}[\]:";'<>?,./]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one special character</li>
                        </ul>
                      </div>

                      <Button
                        blockText
                        isBlue
                        isFullWidth
                        isLarge
                        submit
                        isDisabled={
                          submitLoading
                          || !password
                          || !confirmPassword
                          || !passwordsMatch
                          || !((passwordHealth === 'strong') || (passwordHealth === 'medium'))
                        }
                      >
                        {submitLoading ? 'Updating your Profile...' : 'Next'}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Projector intro video — mounted early to preload, visible only when ready */}
        {isGiftFlow && (
          <div
            className={styles.projectorVideoOverlay}
            style={(showProjectorVideo && videoReady) ? {} : { visibility: 'hidden', position: 'fixed', pointerEvents: 'none' }}
          >
            <div className={styles.projectorVideoModal}>
              <button
                type="button"
                className={styles.projectorVideoSkip}
                onClick={handleDismissProjectorVideo}
              >
                Skip
              </button>
              <video
                className={styles.projectorVideoPlayer}
                src={projectorVideoUrl}
                muted
                playsInline
                preload="auto"
                onCanPlayThrough={() => {
                  setVideoReady(true);
                }}
                onEnded={handleDismissProjectorVideo}
                style={{ width: '100%', height: 'auto' }}
                ref={(el) => {
                  if (el && showProjectorVideo && videoReady) {
                    el.play().catch(() => {});
                  }
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  };

  // Show loader while checking token
  if (isLoading) {
    return (
      <AuthPage>
        {/* {renderEmailConfirmScreen()} */}
        {renderGiftCelebration()}
        {!showGiftPopup && (
          <div className={`${authStyles.authPanel} ${styles.contentWrapper}`} style={{ marginTop: '-20px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <svg className="loading-circle" viewBox="0 0 50 50" style={{ width: '50px', height: '50px' }}>
                <circle
                  className="circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
            </div>
          </div>
        )}
      </AuthPage>
    );
  }

  // Show message if user already has a password
  if (noPassword === false) {
    return (
      <AuthPage>
        {/* {renderEmailConfirmScreen()} */}
        {renderGiftCelebration()}
        {!showGiftPopup && (
          <>
            <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} />
            <div className={`${authStyles.authPanel} ${styles.contentWrapper}`} style={{ marginTop: '-20px' }}>
              <div className={styles.header} style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h1
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#ffffff',
                  }}
                >
                  Password Already Set
                </h1>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#b0b0b0',
                    lineHeight: '1.4',
                    margin: '0 0 24px 0',
                  }}
                >
                  You already have a password. Please login with your existing credentials.
                </p>
                <Button
                  blockText
                  isBlue
                  isFullWidth
                  isLarge
                  onClick={() => navigate('/newSign-in')}
                >
                  Go to Login
                </Button>
              </div>
            </div>
          </>
        )}
      </AuthPage>
    );
  }

  // Show set password form if no_password is true
  return (
    <AuthPage>
      {/* {renderEmailConfirmScreen()} */}
      {renderGiftCelebration()}
      {!showGiftPopup && (
        <>
          {submitLoading && <RotatingLoader dark />}
          <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} />
          <div className={`${authStyles.authPanel} ${styles.contentWrapper}`} style={{ marginTop: '-20px' }}>
            <div className={styles.header} style={{ marginBottom: '20px', textAlign: 'center' }}>
              <h1
                style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#ffffff',
                }}
              >
                Create Password
              </h1>
              <p
                style={{
                  fontSize: '14px',
                  color: '#b0b0b0',
                  lineHeight: '1.4',
                  margin: '0',
                }}
              >
                Create a secure password for your account
              </p>
            </div>

            <form onSubmit={handleNext}>
              <div className={styles.formField} style={{ marginBottom: '16px' }}>
                <Input
                  label="Create Password"
                  type="password"
                  automaticFocus
                  value={password}
                  onInput={handlePasswordInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmPasswordRef.current?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                    if (pastedText.length > 30) {
                      e.preventDefault();
                    }
                    const totalLength = e.target.value.length + pastedText.length;
                    if (totalLength > 30) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const inputPassword = e.target.value;
                    if (inputPassword.length > 30) {
                      const trimmedText = inputPassword.slice(0, 30);
                      e.target.value = trimmedText;
                      setPassword(trimmedText);
                    }
                  }}
                  placeholder="Enter password"
                  minLength={12}
                  maxLength={30}
                  required
                />
              </div>

              <div className={styles.formField} style={{ marginBottom: '16px' }}>
                <Input
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  inputRef={confirmPasswordRef}
                  helperText={
                    confirmPassword
                    && !passwordsMatch && (
                      <span className={authStyles.errorText}>
                        Passwords do not match
                      </span>
                    )
                  }
                />
              </div>

              <div
                className={styles.formField}
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(0, 117, 255, 0.1)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#b0b0b0',
                }}
              >
                <p
                  style={{
                    margin: '0 0 6px 0',
                    fontWeight: '600',
                    color: '#ffffff',
                    fontSize: '13px',
                    textAlign: 'left',
                  }}
                >
                  Password Requirements:
                </p>
                <ul
                  style={{
                    margin: '0',
                    paddingLeft: '18px',
                    lineHeight: '1.6',
                    textAlign: 'left',
                  }}
                >
                  <li style={{ color: password && password.length >= 12 && password.length <= 30 ? '#23e223' : '#b0b0b0' }}>12 - 30 Characters</li>
                  <li style={{ color: password && hasUpperCase ? '#23e223' : '#b0b0b0' }}>Minimum of one uppercase</li>
                  <li style={{ color: password && password.match(/[a-z]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one lowercase</li>
                  <li style={{ color: password && password.match(/[0-9]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one number</li>
                  <li style={{ color: password && password.match(/[!@#$%^&*()_+|~=`{}[\]:";'<>?,./]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one special character</li>
                </ul>
              </div>

              <Button
                blockText
                isBlue
                isFullWidth
                isLarge
                submit
                isDisabled={
                  submitLoading
                  || !password
                  || !confirmPassword
                  || !passwordsMatch
                  || !((passwordHealth === 'strong') || (passwordHealth === 'medium'))
                }
              >
                {submitLoading ? 'Updating your Profile...' : 'Next'}
              </Button>
            </form>
          </div>
        </>
      )}
    </AuthPage>
  );
};
